// ============ Enums ============

export enum ApiKeyType {
  BUSINESS_KEY = 'BUSINESS_KEY',
  SERVICE_KEY = 'SERVICE_KEY'
}

export enum WebhookEventType {
  // Call Events
  CALL_INITIATED = 'CALL_INITIATED',
  CALL_ANSWERED = 'CALL_ANSWERED',
  CALL_COMPLETED = 'CALL_COMPLETED',
  CALL_FAILED = 'CALL_FAILED',
  CALL_TRANSFERRED = 'CALL_TRANSFERRED',

  // SMS Events
  SMS_SENT = 'SMS_SENT',
  SMS_DELIVERED = 'SMS_DELIVERED',
  SMS_FAILED = 'SMS_FAILED',
  SMS_RECEIVED = 'SMS_RECEIVED',

  // WhatsApp Events
  WHATSAPP_MESSAGE_SENT = 'WHATSAPP_MESSAGE_SENT',
  WHATSAPP_MESSAGE_DELIVERED = 'WHATSAPP_MESSAGE_DELIVERED',
  WHATSAPP_MESSAGE_READ = 'WHATSAPP_MESSAGE_READ',
  WHATSAPP_MESSAGE_FAILED = 'WHATSAPP_MESSAGE_FAILED',
  WHATSAPP_MESSAGE_RECEIVED = 'WHATSAPP_MESSAGE_RECEIVED',

  // RCS Events
  RCS_MESSAGE_SENT = 'RCS_MESSAGE_SENT',
  RCS_MESSAGE_DELIVERED = 'RCS_MESSAGE_DELIVERED',
  RCS_MESSAGE_READ = 'RCS_MESSAGE_READ',
  RCS_MESSAGE_FAILED = 'RCS_MESSAGE_FAILED',

  // Campaign Events
  CAMPAIGN_STARTED = 'CAMPAIGN_STARTED',
  CAMPAIGN_COMPLETED = 'CAMPAIGN_COMPLETED',
  CAMPAIGN_FAILED = 'CAMPAIGN_FAILED',

  // IVR Events
  IVR_SESSION_STARTED = 'IVR_SESSION_STARTED',
  IVR_SESSION_COMPLETED = 'IVR_SESSION_COMPLETED',
  IVR_DTMF_RECEIVED = 'IVR_DTMF_RECEIVED',

  // AI Agent Events
  AI_CONVERSATION_STARTED = 'AI_CONVERSATION_STARTED',
  AI_CONVERSATION_COMPLETED = 'AI_CONVERSATION_COMPLETED',
  AI_INTENT_DETECTED = 'AI_INTENT_DETECTED',

  // Billing Events
  CREDITS_LOW = 'CREDITS_LOW',
  CREDITS_EXHAUSTED = 'CREDITS_EXHAUSTED',
  PAYMENT_RECEIVED = 'PAYMENT_RECEIVED',
  INVOICE_GENERATED = 'INVOICE_GENERATED'
}

export enum WebhookDeliveryStatus {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  RETRYING = 'RETRYING'
}

export enum WebhookStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  FAILING = 'FAILING'
}

export enum HttpMethod {
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH'
}

// ============ DTOs ============

export interface ApiKeyDTO {
  id: string;
  name: string;
  keyPrefix?: string; // First 8 chars shown for identification (only on create/regenerate)
  organizationId: string;
  userId: string;
  keyType: ApiKeyType;
  scopes: string[];
  active: boolean;
  expiresAt?: string;
  lastUsedAt?: string;
  description?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface ApiKeyCreateResponse {
  apiKey: ApiKeyDTO;
  secret: string; // Only shown once after creation
}

export interface WebhookDTO {
  id: string;
  name: string;
  url: string;
  method: HttpMethod;
  httpMethod?: string; // Backward compatibility
  organizationId: string;
  userId: string;
  events: WebhookEventType[];
  headers?: Record<string, string>;
  customHeaders?: Record<string, string>; // Backward compatibility
  status: WebhookStatus;
  enabled?: boolean; // Backward compatibility
  retryEnabled: boolean;
  maxRetries: number;
  retryDelaySeconds: number;
  timeoutSeconds: number;
  successCount: number;
  failureCount: number;
  lastTriggeredAt?: string;
  description?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface WebhookDeliveryLogDTO {
  id: string;
  webhookId: string;
  eventType: WebhookEventType;
  status: WebhookDeliveryStatus;
  requestPayload?: string;
  responseStatusCode?: number;
  responseBody?: string;
  responseTimeMs?: number;
  errorMessage?: string;
  retryCount: number;
  nextRetryAt?: string;
  createdAt: string;
}

// ============ Request DTOs ============

export interface CreateApiKeyRequest {
  name: string;
  keyType?: ApiKeyType;
  scopes?: string[];
  expiresAt?: string;
  description?: string;
}

export interface CreateWebhookRequest {
  name: string;
  url: string;
  method?: HttpMethod;
  httpMethod?: string; // Backward compatibility
  events: WebhookEventType[];
  headers?: Record<string, string>;
  customHeaders?: Record<string, string>; // Backward compatibility
  organizationId?: string;
  retryEnabled?: boolean;
  maxRetries?: number;
  retryDelaySeconds?: number;
  timeoutSeconds?: number;
  description?: string;
}

export interface UpdateWebhookRequest {
  name?: string;
  url?: string;
  httpMethod?: string;
  events?: WebhookEventType[];
  customHeaders?: Record<string, string>;
  enabled?: boolean;
  retryEnabled?: boolean;
  maxRetries?: number;
  retryDelaySeconds?: number;
  timeoutSeconds?: number;
  description?: string;
}

export interface TestWebhookRequest {
  eventType: WebhookEventType;
}

// Webhook Log (used in Webhooks.tsx)
export interface WebhookLogDTO {
  id: string;
  webhookId: string;
  eventType: WebhookEventType;
  success: boolean;
  responseStatus?: number;
  responseTimeMs?: number;
  errorMessage?: string;
  retryAttempt: number;
  createdAt: string;
}

// Test webhook response
export interface TestWebhookResponse {
  success: boolean;
  responseStatus?: number;
  responseTimeMs?: number;
  errorMessage?: string;
}

// ============ Statistics ============

export interface ApiKeyStatsDTO {
  total: number;
  active: number;
  inactive: number;
  expired: number;
}

export interface WebhookStatsDTO {
  totalWebhooks: number;
  activeWebhooks: number;
  failingWebhooks: number;
  totalDeliveries: number;
  successfulDeliveries: number;
  failedDeliveries: number;
  deliveriesLast24h: number;
  averageResponseTimeMs: number;
  eventBreakdown: Array<{
    eventType: WebhookEventType;
    count: number;
  }>;
}

// ============ API Documentation Types ============

export interface ApiEndpoint {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  description: string;
  authentication: 'API Key' | 'Bearer Token' | 'None';
  requiredScopes?: string[];
  requestBody?: ApiRequestSchema;
  queryParams?: ApiParameter[];
  pathParams?: ApiParameter[];
  responseSchema?: ApiResponseSchema;
  exampleRequest?: string;
  exampleResponse?: string;
  rateLimitInfo?: string;
}

export interface ApiParameter {
  name: string;
  type: string;
  required: boolean;
  description: string;
  example?: string;
  enum?: string[];
}

export interface ApiRequestSchema {
  contentType: string;
  schema: Record<string, ApiParameter>;
}

export interface ApiResponseSchema {
  statusCode: number;
  contentType: string;
  schema: Record<string, unknown>;
}

export interface ApiCategory {
  id: string;
  name: string;
  description: string;
  endpoints: ApiEndpoint[];
}
